import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ComprobantesService } from './comprobantes.service';
import { EmpresaService } from './empresa.service';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class ImpresionService {

  constructor(
    private comprobanteService: ComprobantesService,
    private empresaService: EmpresaService
  ) { }

  /**
   * Genera el contenido HTML para impresión de comprobantes
   * @param comprobanteData Datos del comprobante
   * @param tipoComprobante Tipo de comprobante (Factura, Remito, etc.)
   * @param empresaData Datos de la empresa
   * @param contextData Datos del contexto del componente
   * @returns HTML string para impresión
   */
  generarContenidoImpresion(comprobanteData: any, tipoComprobante: string, empresaData: any, contextData: any): string {
    const fecha = new Date().toLocaleDateString('es-AR');
    const hora = new Date().toLocaleTimeString('es-AR');
    
    // Información de la empresa
    const nombreEmpresa = empresaData?.fantasyName || empresaData?.socialReason || 'KOCURSOFT - ERP PYMES';
    const cuitEmpresa = empresaData?.documentNumber || '';

    // Información del comprobante - obtener desde los datos del voucher
    let comprobanteConLetra = tipoComprobante;
    let numeroComprobanteFormateado = '';
    
    // PRIORIDAD 1: Obtener el nombre del comprobante desde el header.receiptBook
    if (comprobanteData?.header?.receiptBook?.description) {
      comprobanteConLetra = comprobanteData.header.receiptBook.description;
    }
    // PRIORIDAD 2: Intentar obtener el tipo de comprobante y letra desde el contexto
    else if (contextData?.typeComprobanteDB) {
      const typeDB = contextData.typeComprobanteDB;
      let nombreTipo = typeDB.name || typeDB.description || tipoComprobante;
      
      // Limpiar el nombre del tipo si contiene "Comprobante"
      nombreTipo = nombreTipo.replace(/^Comprobante\s+/i, '');
      
      let letra = typeDB.voucherTypesCode?.name || '';
      
      comprobanteConLetra = letra ? `${nombreTipo} ${letra}` : nombreTipo;
    } 
    // PRIORIDAD 3: Usar datos del voucher si están disponibles
    else if (comprobanteData?.voucherType) {
      const voucherType = comprobanteData.voucherType;
      let nombreTipoComprobante = voucherType.name || voucherType.description || tipoComprobante;
      
      // Limpiar el nombre del tipo si contiene "Comprobante"
      nombreTipoComprobante = nombreTipoComprobante.replace(/^Comprobante\s+/i, '');
      
      let letra = voucherType.voucherTypesCode?.name || '';
      
      comprobanteConLetra = letra ? `${nombreTipoComprobante} ${letra}` : nombreTipoComprobante;
    } 
    // PRIORIDAD 4: Último fallback desde talonarios del contexto
    else {
      if (contextData?.talariosFiltrados && contextData?.form) {
        const talonarioId = contextData.form.get('talonario')?.value;
        const talonarioSeleccionado = contextData.talariosFiltrados.find(t => t.id === talonarioId);
        
        if (talonarioSeleccionado?.voucherType?.voucherTypesCode?.name) {
          const letra = talonarioSeleccionado.voucherType.voucherTypesCode.name;
          comprobanteConLetra = `${tipoComprobante} ${letra}`;
        }
      }
    }
    
    // Formatear número de comprobante desde el header
    if (comprobanteData?.header) {
      const header = comprobanteData.header;
      
      // Obtener el punto de venta y número desde el header
      if (header.number) {
        // Intentar obtener el punto de venta desde el contexto del talonario
        let puntoVenta = '0001'; // Valor por defecto
        
        // Primero intentar desde el receiptBook si tiene información del punto de venta
        if (header.receiptBook?.salesPointsId) {
          puntoVenta = ('0000' + header.receiptBook.salesPointsId.toString()).slice(-4);
        }
        // Si no, intentar desde contextData
        else if (contextData?.talariosFiltrados && header.receiptBookId) {
          const talonario = contextData.talariosFiltrados.find(t => t.id === header.receiptBookId);
          if (talonario?.salesPointsId) {
            puntoVenta = ('0000' + talonario.salesPointsId.toString()).slice(-4);
          }
        }
        
        const numero = ('00000000' + header.number.toString()).slice(-8);
        numeroComprobanteFormateado = `${puntoVenta}-${numero}`;
      }
    }
    
    // Fallback para número de comprobante desde contexto si no se obtuvo del header
    if (!numeroComprobanteFormateado && contextData?.talariosFiltrados && contextData?.form) {
      const talonarioId = contextData.form.get('talonario')?.value;
      const talonarioSeleccionado = contextData.talariosFiltrados.find(t => t.id === talonarioId);
      
      if (talonarioSeleccionado) {
        const puntoVenta = ('0000' + (talonarioSeleccionado.salesPointsId || 1).toString()).slice(-4);
        const numero = ('00000000' + (talonarioSeleccionado.currentNumber || 1).toString()).slice(-8);
        numeroComprobanteFormateado = `${puntoVenta}-${numero}`;
        
        // También obtener la letra si no se obtuvo antes
        if (comprobanteConLetra === tipoComprobante) {
          let letra = '';
          if (contextData?.typeComprobanteDB?.voucherTypesCode?.name) {
            letra = contextData.typeComprobanteDB.voucherTypesCode.name;
          } else if (talonarioSeleccionado.voucherType?.voucherTypesCode?.name) {
            letra = talonarioSeleccionado.voucherType.voucherTypesCode.name;
          }
          
          comprobanteConLetra = letra ? `${tipoComprobante} ${letra}` : tipoComprobante;
        }
      }
    }
    
    // Si no se pudo formatear el número, intentar obtener al menos el número básico
    if (!numeroComprobanteFormateado && comprobanteData?.header?.number) {
      numeroComprobanteFormateado = `0001-${('00000000' + comprobanteData.header.number.toString()).slice(-8)}`;
    }

    // Información del cliente
    const cliente = contextData?.cliente || comprobanteData?.client || {};
    const nombreCliente = cliente?.companyName || (cliente?.firstName ? `${cliente.firstName} ${cliente.lastName}` : '') || 'Cliente';
    const cuitCliente = cliente?.documentNumber || '';
    const direccionCliente = cliente?.address || '';

    // Obtener productos del comprobante o del contexto
    const productos = comprobanteData?.detail || contextData?.articulosList || [];

    // Calcular totales de bonificación
    let bonificacionTotal = 0;

    // Generar filas de productos
    let filasProductos = '';
    productos.forEach((producto) => {
      // Obtener datos del producto desde diferentes estructuras posibles
      let codigo = '';
      let descripcion = '';
      let cantidad = 0;
      let precioUnitario = 0;
      let bonificacionMonto = 0;
      
      // PRIORIDAD 1: Verificar si hay datos del artículo en producto.variant.article (estructura de la tabla detalles)
      if (producto.variant?.article) {
        codigo = producto.variant?.barcode || producto.variant?.article?.code || producto.code || '';
        descripcion = producto.variant?.article?.name || producto.variant?.article?.description || producto.description || '';
        cantidad = Number(producto.quantity || 0);
        precioUnitario = Number(producto.unitPrice || 0);
        bonificacionMonto = Number(producto.bonusAmount || producto.discount || producto.bonificacion || 0);
      }
      // PRIORIDAD 2: Verificar si hay datos del artículo en producto.article (estructura alternativa del API)
      else if (producto.article) {
        codigo = producto.article.code || producto.code || '';
        descripcion = producto.article.description || producto.article.name || producto.description || '';
        cantidad = Number(producto.quantity || 0);
        precioUnitario = Number(producto.unitPrice || 0);
        bonificacionMonto = Number(producto.bonusAmount || producto.discount || producto.bonificacion || 0);
      }
      // PRIORIDAD 3: Datos directos en el detalle (code y description están en la tabla detalles)
      else if (producto.code || producto.description) {
        codigo = producto.code || '';
        descripcion = producto.description || '';
        cantidad = Number(producto.quantity || 0);
        precioUnitario = Number(producto.unitPrice || 0);
        bonificacionMonto = Number(producto.bonusAmount || producto.discount || producto.bonificacion || 0);
      }
      // FALLBACK: Intentar extraer de cualquier campo disponible
      else {
        codigo = producto.code || producto.articleCode || '';
        descripcion = producto.description || producto.name || producto.articleDescription || '';
        cantidad = Number(producto.quantity || producto.cant || 0);
        precioUnitario = Number(producto.unitPrice || producto.precio || 0);
        bonificacionMonto = Number(producto.bonusAmount || producto.discount || producto.bonificacion || 0);
      }
      
      // Acumular bonificación total
      bonificacionTotal += bonificacionMonto;
      
      // Usar el subtotal que ya viene calculado con bonificación aplicada, o calcular si no está disponible
      const subtotalProducto = Number(producto.subtotalWithIva || producto.subtotal || (cantidad * precioUnitario));

      filasProductos += `
        <tr>
          <td>${codigo}</td>
          <td>${descripcion}</td>
          <td style="text-align: center;">${cantidad}</td>
          <td style="text-align: right;">$ ${precioUnitario.toFixed(2)}</td>
          <td style="text-align: right;">$ ${bonificacionMonto.toFixed(2)}</td>
          <td style="text-align: right;">$ ${subtotalProducto.toFixed(2)}</td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${comprobanteConLetra}</title>
        <meta charset="utf-8">
        <style>
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            margin: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #102dd3;
            padding-bottom: 10px;
          }
          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #102dd3;
            margin-bottom: 5px;
          }
          .voucher-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
          }
          .client-info {
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th {
            background-color: #f4f3ef;
            padding: 8px;
            border: 1px solid #ddd;
            font-weight: bold;
          }
          td {
            padding: 5px;
            border-bottom: 1px solid #ddd;
          }
          .totals {
            text-align: right;
            margin-top: 20px;
          }
          .total-line {
            margin: 5px 0;
          }
          .final-total {
            font-size: 16px;
            font-weight: bold;
            color: #102dd3;
            border-top: 2px solid #102dd3;
            padding-top: 10px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${nombreEmpresa}</div>
          <div style="font-size: 11px; margin: 5px 0;">
            ${cuitEmpresa ? `CUIT: ${cuitEmpresa}` : ''}
          </div>
          <div style="font-size: 16px; font-weight: bold; color: #ef8157; margin-top: 10px;">${comprobanteConLetra}</div>
        </div>
        
        <div class="voucher-info">
          <div>
            <strong>N° ${numeroComprobanteFormateado || 'S/N'}</strong>
          </div>
          <div>
            <strong>Fecha:</strong> ${fecha}<br>
            <strong>Hora:</strong> ${hora}
          </div>
        </div>
        
        <div class="client-info">
          <strong>Cliente:</strong> ${nombreCliente}<br>
          ${cuitCliente ? `<strong>CUIT/DNI:</strong> ${cuitCliente}<br>` : ''}
          ${direccionCliente ? `<strong>Dirección:</strong> ${direccionCliente}` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 10%;">Código</th>
              <th style="width: 35%;">Descripción</th>
              <th style="width: 8%;">Cant.</th>
              <th style="width: 12%;">P. Unit.</th>
              <th style="width: 12%;">Bonificación</th>
              <th style="width: 13%;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${filasProductos}
          </tbody>
        </table>

        ${contextData?.typeComprobante !== 'remito' ? `
        <div class="totals">
          <div class="total-line"><strong>Subtotal:</strong> $ ${(() => {
            // Calcular subtotal como cantidad × precio unitario (SIN bonificación aplicada)
            let subtotalCalculado = 0;
            if (productos && productos.length > 0) {
              subtotalCalculado = productos.reduce((total, producto) => {
                const cantidad = Number(producto.quantity || 0);
                const precioUnitario = Number(producto.unitPrice || 0);
                return total + (cantidad * precioUnitario);
              }, 0);
            }
            return subtotalCalculado > 0 ? subtotalCalculado.toFixed(2) : (Number(comprobanteData?.netAmount || contextData?.subtotal) || 0).toFixed(2);
          })()}</div>
          <div class="total-line"><strong>Bonificación:</strong> $ ${(bonificacionTotal || Number(contextData?.bonificacion) || 0).toFixed(2)}</div>
          <div class="total-line final-total"><strong>TOTAL:</strong> $ ${(() => {
            // El total debe ser subtotal - bonificación
            let subtotalCalculado = 0;
            if (productos && productos.length > 0) {
              subtotalCalculado = productos.reduce((total, producto) => {
                const cantidad = Number(producto.quantity || 0);
                const precioUnitario = Number(producto.unitPrice || 0);
                return total + (cantidad * precioUnitario);
              }, 0);
              const bonificacion = bonificacionTotal || Number(contextData?.bonificacion) || 0;
              return (subtotalCalculado - bonificacion).toFixed(2);
            } else {
              const subtotal = Number(comprobanteData?.netAmount || contextData?.subtotal) || 0;
              const bonificacion = bonificacionTotal || Number(contextData?.bonificacion) || 0;
              return (subtotal - bonificacion).toFixed(2);
            }
          })()}</div>
        </div>
        ` : ''}

        <!-- Comentario del comprobante -->
        ${(() => {
          const comentario = comprobanteData?.comment || contextData?.form?.controls?.comentario?.value || '';
          return comentario ? `
            <div style="margin-top: 15px; padding: 10px; border: 1px solid #F1EAE0; border-radius: 4px; background-color: #f8f9fa;">
              <strong style="color: #66615b;">Comentarios:</strong><br>
              <span style="color: #2c2c2c; font-size: 12px;">${comentario}</span>
            </div>
          ` : '';
        })()}

        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.close()" style="padding: 10px 20px; background: #66615b; color: white; border: none; border-radius: 5px; cursor: pointer;">
            ✖️ Cerrar
          </button>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Imprime un comprobante por su ID
   * @param comprobanteId ID del comprobante a imprimir
   * @param tipoOperacion Tipo de operación ('ventas' o 'compras')
   */
  imprimirComprobante(comprobanteId: number, tipoOperacion: string = 'ventas'): void {
    if (!comprobanteId) {
      Swal.fire({
        title: 'Error',
        text: 'ID de comprobante no válido',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef8157'
      });
      return;
    }

    // Obtener datos del comprobante
    this.comprobanteService.getComprobanteById(comprobanteId).subscribe({
      next: (comprobanteData) => {
        if (comprobanteData) {
          // Determinar el tipo de comprobante basado en los datos
          const tipoComprobante = this.determinarTipoComprobante(comprobanteData);
          
          // Calcular bonificación total desde los detalles del comprobante
          let bonificacionTotal = 0;
          if (comprobanteData.detail && Array.isArray(comprobanteData.detail)) {
            bonificacionTotal = comprobanteData.detail.reduce((total, item) => {
              return total + Number(item.bonusAmount || item.discount || item.bonificacion || 0);
            }, 0);
          }
          
          // Obtener información de la empresa
          this.empresaService.getEmpresaConfig().pipe(
            catchError(error => {
              console.warn('No se pudo obtener información de la empresa, usando valores por defecto:', error);
              return of({
                socialReason: 'KOCURSOFT - ERP PYMES',
                commercialAddress: '',
                documentNumber: '',
                phone: '',
                email: ''
              });
            })
          ).subscribe(empresaData => {
            // Crear contexto básico para la impresión
            const contextData = {
              typeComprobante: tipoOperacion,
              subtotal: Number(comprobanteData.netAmount || 0),
              bonificacion: bonificacionTotal,
              impuestos: Number(comprobanteData.ivaAmount || 0),
              total: Number(comprobanteData.totalAmount || 0)
            };
            
            const contenidoImpresion = this.generarContenidoImpresion(comprobanteData, tipoComprobante, empresaData, contextData);
            
            // Mostrar diálogo de confirmación y ejecutar impresión
            Swal.fire({
              title: 'Imprimir Comprobante',
              text: `¿Deseas imprimir este ${tipoComprobante.toLowerCase()}?`,
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Imprimir',
              cancelButtonText: 'Cancelar',
              confirmButtonColor: '#102dd3',
              cancelButtonColor: '#6c757d'
            }).then((result) => {
              if (result.isConfirmed) {
                this.ejecutarImpresion(contenidoImpresion);
              }
            });
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: 'No se pudieron obtener los datos del comprobante',
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#ef8157'
          });
        }
      },
      error: (error) => {
        console.error('Error al obtener datos del comprobante:', error);
        Swal.fire({
          title: 'Error',
          text: 'Error al cargar los datos del comprobante para impresión',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#ef8157'
        });
      }
    });
  }

  /**
   * Determina el tipo de comprobante basado en los datos del comprobante
   * @param comprobanteData Datos del comprobante
   * @returns Tipo de comprobante como string
   */
  private determinarTipoComprobante(comprobanteData: any): string {
    // Mapeo de tipos comunes
    const tiposMap = {
      'factura': 'Factura',
      'presupuesto': 'Presupuesto',
      'pedido': 'Pedido',
      'remito': 'Remito',
      'notadecredito': 'Nota de Crédito',
      'notadedebito': 'Nota de Débito',
      'recibo': 'Recibo'
    };

    // Intentar determinar por el voucherType
    if (comprobanteData.voucherType?.name) {
      const voucherTypeName = comprobanteData.voucherType.name.toLowerCase();
      for (const [key, value] of Object.entries(tiposMap)) {
        if (voucherTypeName.includes(key)) {
          return value;
        }
      }
    }

    // Intentar determinar por el voucherTypesCode
    if (comprobanteData.voucherType?.voucherTypesCode?.name) {
      const codeTypeName = comprobanteData.voucherType.voucherTypesCode.name.toLowerCase();
      for (const [key, value] of Object.entries(tiposMap)) {
        if (codeTypeName.includes(key)) {
          return value;
        }
      }
    }

    // Fallback genérico
    return 'Comprobante';
  }

  /**
   * Ejecuta la impresión abriendo una ventana nueva
   * @param contenidoHTML Contenido HTML a imprimir
   */
  private ejecutarImpresion(contenidoHTML: string): void {
    const ventanaImpresion = window.open('', '_blank');
    
    if (ventanaImpresion) {
      ventanaImpresion.document.write(contenidoHTML);
      ventanaImpresion.document.close();
      
      // Esperar a que la ventana esté lista y luego imprimir
      ventanaImpresion.onload = () => {
        setTimeout(() => {
          ventanaImpresion.print();
          ventanaImpresion.close();
        }, 250);
      };
    } else {
      Swal.fire({
        title: 'Error',
        text: 'No se pudo abrir la ventana de impresión. Verifique que las ventanas emergentes estén habilitadas.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#fbc658'
      });
    }
  }
}
